import { onBeforeUnmount, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import { isAnalyticsAllowed } from '@/utils/use-cookie-consent';

export interface UseGtmOptions {
	dataLayerName?: string;
	autoTrackPageviews?: boolean;
	sendInitialPageview?: boolean;
	title?: string;
}

export type DataLayerPayload = Record<string, unknown> & {
	event?: string;
};

export interface UseGtm {
	pushRaw: (obj: DataLayerPayload) => void;

	pushEvent: (eventName: string, payload?: Record<string, unknown>) => void;

	trackPageView: (info?: {
		path?: string;
		title?: string;
		location?: string;
		referrer?: string;
	}) => void;

	setUser: (
		userId: string | number | null,
		traits?: Record<string, unknown>,
	) => void;
}

let _dataLayerName = 'dataLayer';
let subscribers = 0;
let unlisten: (() => void) | null = null;
let autoTrackingConfigured = false;
let prevHref: string | undefined =
	typeof document !== 'undefined' ? document.referrer || undefined : undefined;

function getDataLayer(name = _dataLayerName): unknown[] | null {
	if (typeof window === 'undefined') return null;
	const w = window as unknown as Record<string, unknown>;
	if (!w[name]) w[name] = [];
	return w[name] as unknown[];
}

function pushRaw(obj: DataLayerPayload) {
	// Nothing is queued before consent, or GTM would replay it once loaded.
	if (!isAnalyticsAllowed()) return;
	const dl = getDataLayer();
	if (!dl) return;
	dl.push(obj);
}

function pushEvent(eventName: string, payload: Record<string, unknown> = {}) {
	pushRaw({ event: eventName, ...payload });
}

function trackPageView(
	info: {
		path?: string;
		title?: string;
		location?: string;
		referrer?: string;
	} = {},
) {
	const loc = typeof window !== 'undefined' ? window.location : null;
	const pageReferrer =
		info.referrer ??
		prevHref ??
		(typeof document !== 'undefined'
			? document.referrer || undefined
			: undefined);

	pushEvent('virtualPageview', {
		page_path: info.path ?? loc?.pathname,
		pageUrl: info.location ?? loc?.href,
		pageTitle:
			info.title ??
			(typeof document !== 'undefined' ? document.title : undefined),
		previousURL: pageReferrer,
	});

	if (loc?.href) prevHref = loc.href;
}

function setUser(
	userId: string | number | null,
	traits: Record<string, unknown> = {},
) {
	pushRaw({
		user_id: userId ?? undefined,
		user_properties: traits,
	});
}

export function useGtm(options?: UseGtmOptions): UseGtm {
	if (options?.dataLayerName) _dataLayerName = options.dataLayerName;
	const autoTrack = options?.autoTrackPageviews ?? true;
	const sendInitial = options?.sendInitialPageview ?? true;

	onMounted(() => {
		getDataLayer();
		subscribers += 1;

		if (autoTrack) {
			if (sendInitial && subscribers === 1 && !autoTrackingConfigured) {
				trackPageView();
			} else if (!unlisten && !autoTrackingConfigured && subscribers !== 1) {
				unlisten = router.on('success', () => trackPageView());
				autoTrackingConfigured = true;
			}
		}
	});

	onBeforeUnmount(() => {
		subscribers = Math.max(0, subscribers - 1);
		if (subscribers === 0 && unlisten) {
			unlisten();
			unlisten = null;
			autoTrackingConfigured = false;
		}
	});

	return { pushRaw, pushEvent, trackPageView, setUser };
}

export function useGtmApi(opts: Pick<UseGtmOptions, 'dataLayerName'> = {}) {
	if (opts.dataLayerName) _dataLayerName = opts.dataLayerName;
	getDataLayer();
	return { pushRaw, pushEvent, trackPageView };
}
